// Direction B "Ink Header" welcome form. 4 fields stacked vertically, mono
// uppercase labels (9.5px / 0.16em tracking), 38px tall inputs, brand-pink
// CTA. Below the CTA: a TCPA-style consent paragraph naming the venue and
// linking to Mikla's privacy policy. The "Avg reply 47s · Live in 12 minutes"
// trust line from the original design was replaced by this consent block to
// satisfy SMS/email consent requirements (10DLC + general CAN-SPAM).
//
// Per spec §3.2 + Direction B handoff:
//   - Eyebrow: "01 · 4-STEP INTRO" — `01` is brand pink, `·` is muted-300.
//   - Title (22px / 600 / -0.022em): "Tell us about your event"
//   - Sub (13.5px / muted-500): "A few details so I can check availability and book a tour. Takes 20 seconds."
//   - Fields in order: name, email, phone, event type
//   - Event type is a CustomSelect (white menu, 10px radius, soft shadow,
//     brand-red check on selected, smart up/down placement). See
//     CustomSelect.tsx for the full behavior.
//   - CTA: brand-pink "Start chat →" with the cta inset-highlight shadow
//
// Phone is captured client-side + cookie + sent to backend as `lead_phone`.
// The backend Pydantic model (`ChatSessionRequest`) silently ignores extras
// (default `extra='ignore'`), so the field round-trips harmlessly until the
// backend wires up persistence in a follow-up commit.

import { useState, FormEvent } from 'react'
import { useLeadCookie } from '../hooks/useLeadCookie'
import { ARROW_SVG } from '../icons/icons'
import { CustomSelect } from './CustomSelect'

export interface DomainOption {
  value: string
  display_name: string
}

export interface PreChatFormSubmit {
  name: string
  email: string
  phone: string
  event_type: string | null
}

export interface PreChatFormProps {
  domainOptions: DomainOption[]
  // Venue display name used in the consent paragraph below the CTA. App
  // resolves this from probe.vendor_info.business_name; falls back to a
  // neutral "the venue" if not yet available.
  venueName: string
  // Vendor-specific field label and form heading from the brain. null /
  // undefined / blank = legacy vendor: render today's wedding strings.
  fieldLabel?: string | null
  heading?: string | null
  onSubmit: (data: PreChatFormSubmit) => void
}

const PRIVACY_POLICY_URL = 'https://mikla.ai/privacy'
const DEFAULT_FIELD_LABEL = 'Event type'
const DEFAULT_HEADING = 'Tell us about your event'

export function PreChatForm({
  domainOptions,
  venueName,
  fieldLabel,
  heading,
  onSubmit,
}: PreChatFormProps) {
  // typeof guards: the props come from a JSON response, so a non-string value
  // must fall back to the default rather than throw on .trim().
  const label = (typeof fieldLabel === 'string' ? fieldLabel.trim() : '') || DEFAULT_FIELD_LABEL
  const headingText = (typeof heading === 'string' ? heading.trim() : '') || DEFAULT_HEADING
  // Default label keeps today's literal placeholder; the neutral question form
  // ("What can we help with?") cannot be lowercased into a sentence.
  const placeholder =
    label === DEFAULT_FIELD_LABEL
      ? 'Select event type…'
      : label.endsWith('?')
        ? 'Select an option…'
        : 'Select ' + label.toLowerCase() + '…'
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  // When the vendor exposes exactly ONE event type (e.g. a wedding-only
  // venue), pre-select it so the lead isn't forced to open a single-item
  // menu. With 2+ options we leave it blank so the placeholder shows and the
  // lead actively chooses. domainOptions is fixed for this form's lifetime
  // (App resolves it from the probe before mounting), so a lazy initializer
  // is sufficient — no effect needed.
  const [eventType, setEventType] = useState(
    () => (domainOptions.length === 1 ? domainOptions[0].value : ''),
  )
  const [phoneError, setPhoneError] = useState(false)
  const cookie = useLeadCookie()

  const handle = (e: FormEvent) => {
    e.preventDefault()
    const trimmedName = name.trim()
    const trimmedEmail = email.trim()
    const trimmedPhone = phone.trim()
    if (!trimmedName || !trimmedEmail) return
    // Phone is required per Direction B handoff; we don't enforce a regex
    // (per spec recommendation #4 — leave format raw; validate server-side).
    if (!trimmedPhone) {
      setPhoneError(true)
      return
    }
    setPhoneError(false)
    // Empty string = lead didn't pick from a multi-option dropdown (or the
    // vendor has no user-facing domains, so no dropdown rendered). Single-
    // domain vendors arrive here with the one option already pre-selected.
    // Anything non-empty is a concrete vendor domain already validated
    // against agent_domains at the router boundary, so it carries high
    // domain-confidence downstream.
    const submitEventType = eventType === '' ? null : eventType
    cookie.write({
      name: trimmedName,
      email: trimmedEmail,
      phone: trimmedPhone,
      event_type: submitEventType ?? undefined,
    })
    onSubmit({
      name: trimmedName,
      email: trimmedEmail,
      phone: trimmedPhone,
      event_type: submitEventType,
    })
  }

  return (
    <form
      onSubmit={handle}
      className="flex-1 overflow-y-auto px-5 pt-[18px] pb-3.5 flex flex-col gap-3.5 bg-paper"
      data-testid="pre-chat-form"
    >
      {/* ── Intro: title ─────────────────────────────────────────────── */}
      <h2 className="text-[22px] font-semibold leading-[1.15] text-ink m-0" style={{ letterSpacing: '-0.022em', textWrap: 'balance' as React.CSSProperties['textWrap'] }}>
        {headingText}
      </h2>

      {/* ── Fields ──────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-2">
        <Field label="Your name">
          <input
            data-testid="name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="John Doe"
            required
            className={inputClasses}
          />
        </Field>
        <Field label="Email address">
          <input
            data-testid="email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="john@example.com"
            required
            className={inputClasses}
          />
        </Field>
        <Field label="Phone number" error={phoneError ? 'Phone number is required' : undefined}>
          <input
            data-testid="phone-input"
            type="tel"
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value)
              if (phoneError) setPhoneError(false)
            }}
            placeholder="(415) 555-0142"
            required
            className={
              phoneError ? `${inputClasses} border-accent focus:border-accent` : inputClasses
            }
          />
        </Field>
        {domainOptions.length > 0 && (
          <Field label={label}>
            <CustomSelect
              testId="event-type-select"
              ariaLabel={label}
              value={eventType}
              onChange={setEventType}
              placeholder={placeholder}
              options={domainOptions.map((o) => ({ value: o.value, label: o.display_name }))}
            />
          </Field>
        )}
      </div>

      {/* ── CTA ──────────────────────────────────────────────────────── */}
      {/* CRITICAL: `flex-shrink-0` is load-bearing. The form is a flex
          column with `flex-1 overflow-y-auto`; without flex-shrink-0 the
          button collapses to its content height (~24px) regardless of any
          h-* class set on it. We chased this for half a dozen "h bump"
          iterations before finding it. Don't remove flex-shrink-0.
          Sized 56px / 16px-600 type / 12px radius / 14px icon — chunky
          conversion CTA that matches the Direction B canvas presentation. */}
      <button
        data-testid="form-submit"
        type="submit"
        className="flex-shrink-0 h-14 mt-1 bg-accent hover:bg-accent-hover text-white rounded-[12px] font-semibold text-base inline-flex items-center justify-center gap-2 shadow-cta transition-colors"
      >
        Start chat
        <span
          dangerouslySetInnerHTML={{ __html: ARROW_SVG }}
          className="w-3.5 h-3.5 inline-block"
        />
      </button>

      {/* ── Consent paragraph (TCPA + privacy) ──────────────────────────
          Sits directly under the CTA. Standard small-print legal treatment:
          11.5px / muted-500 / line-height generous. The venue name is
          interpolated from vendor_info; "Privacy Policy" links to Mikla's
          policy URL (opens in a new tab so the chat session isn't lost). */}
      <p
        data-testid="consent-text"
        className="text-[11.5px] text-muted-500 leading-[1.5] mt-1.5 m-0"
      >
        {'By clicking "Start chat" you consent to receive text messages and emails from '}
        <span className="text-ink font-medium">{venueName}</span>
        {' at the number and email you provided and to real-time access and monitoring of your chats by Mikla. Responses may be automated. Msg frequency varies. Std msg/data rates apply. Text STOP to opt out; HELP for help. See our '}
        <a
          href={PRIVACY_POLICY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-ink underline decoration-muted-300 hover:decoration-ink"
        >
          Privacy Policy
        </a>
        {' for details.'}
      </p>
    </form>
  )
}

const inputClasses = [
  // 40px tall to match the Direction B canvas (`.welcome-input` renders at
  // 40px in the rendered HTML; the README's "40px" is the truth, not 38px).
  'h-10 px-3.5 border border-border rounded-lg',
  'font-sans text-sm text-ink bg-paper',
  'outline-none transition-colors',
  'placeholder:text-muted-400',
  'focus:border-ink focus:ring-[3px] focus:ring-accent/10',
].join(' ')

interface FieldProps {
  label: string
  error?: string
  children: React.ReactNode
}

function Field({ label, error, children }: FieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <label className="font-mono text-[9.5px] font-medium uppercase tracking-eyebrow text-muted-500">
        {label}
      </label>
      {children}
      {error && (
        <span className="text-[12px] text-accent mt-0.5">{error}</span>
      )}
    </div>
  )
}
